import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { JobCard } from '@/components/JobCard';
import { useJobs } from '@/hooks/useJobs';
import { JobStatus } from '@recruitai/shared';
import { AppLayout } from '@/components/AppLayout';

export default function JobsListPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<JobStatus | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const { jobs, isLoading, error } = useJobs(statusFilter);

  // Filter jobs by search query
  const filteredJobs = jobs.filter(job => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      job.title.toLowerCase().includes(query) ||
      job.description.toLowerCase().includes(query) ||
      job.requirements.some(req => req.toLowerCase().includes(query))
    );
  });

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold mb-2">Vagas</h2>
            <p className="text-muted-foreground">
              Gerencie as vagas abertas da sua organização
            </p>
          </div>
          <Button onClick={() => navigate('/jobs/new')}>
            + Nova Vaga
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Filtros</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search Input */}
            <div>
              <Input
                type="text"
                placeholder="🔍 Buscar por título, descrição ou requisitos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Status Filters */}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={statusFilter === undefined ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(undefined)}
              >
                Todas
              </Button>
              <Button
                variant={statusFilter === JobStatus.DRAFT ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(JobStatus.DRAFT)}
              >
                Rascunhos
              </Button>
              <Button
                variant={statusFilter === JobStatus.OPEN ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(JobStatus.OPEN)}
              >
                Abertas
              </Button>
              <Button
                variant={statusFilter === JobStatus.CLOSED ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(JobStatus.CLOSED)}
              >
                Fechadas
              </Button>
            </div>

            {/* Results count */}
            {searchQuery && (
              <p className="text-sm text-muted-foreground">
                {filteredJobs.length} resultado{filteredJobs.length !== 1 ? 's' : ''} encontrado{filteredJobs.length !== 1 ? 's' : ''}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Jobs Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Carregando vagas...</p>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-destructive mb-4">
                Erro ao carregar vagas: {error.message}
              </p>
              <Button onClick={() => window.location.reload()}>
                Tentar Novamente
              </Button>
            </CardContent>
          </Card>
        ) : filteredJobs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">
                {searchQuery ? 'Nenhuma vaga encontrada com os filtros aplicados' : 'Nenhuma vaga encontrada'}
              </p>
              {!searchQuery && (
                <Button onClick={() => navigate('/jobs/new')}>
                  Criar Primeira Vaga
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredJobs.map((job) => (
              <JobCard key={job.jobId} job={job} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
